// Compact connection indicators (icons) for the top bar.
import type { SignalingStatus } from '../signaling';

export type IcePath = 'lan' | 'host' | 'stun' | 'turn' | 'unknown';
export type IceIpFamily = 'ipv6' | 'ipv4' | '?' | null;
export type ConnTone = 'good' | 'warn' | 'bad' | 'idle';

export interface StatusPillsProps {
  session: {
    status: SignalingStatus;
    hostOnline: boolean;
    phase: string;
  };
  busy: boolean;
  /** True once REST login succeeded — signaling is green until a PC is selected. */
  signedIn: boolean;
  /** When set, re-evaluate signaling from the live WebSocket (after PC pick). */
  pcSessionActive: boolean;
  icePath?: IcePath | null;
  iceDialPhase?: 'stun' | 'turn' | null;
  iceIpFamily?: IceIpFamily;
  /** User-facing line under the P2P icon while negotiating. */
  p2pDetail?: string | null;
}

function toneClass(tone: ConnTone): string {
  return `conn-icon tone-${tone}`;
}

function signalingTone(
  status: SignalingStatus,
  signedIn: boolean,
  pcSessionActive: boolean
): ConnTone {
  if (!signedIn) return 'idle';
  if (!pcSessionActive) return 'good';
  if (status === 'open') return 'good';
  if (status === 'connecting') return 'warn';
  return 'bad';
}

function hostTone(
  hostOnline: boolean,
  pcSessionActive: boolean,
  signalingOpen: boolean,
  phase: string
): ConnTone {
  if (!pcSessionActive) return 'idle';
  // P2P linked — host is reachable even if signaling presence lags or WS reconnects.
  if (phase === 'linked') return 'good';
  if (hostOnline) return 'good';
  if (signalingOpen) return 'warn';
  return 'bad';
}

function p2pTone(phase: string, busy: boolean, pcSessionActive: boolean, hostOnline: boolean): ConnTone {
  if (!pcSessionActive) return 'idle';
  if (phase === 'linked') return 'good';
  if (busy || (hostOnline && phase !== 'linked')) return 'warn';
  return 'bad';
}

/** L = LAN, H = direct host candidates, S = STUN, T = TURN relay. */
function pathBadge(path: IcePath, ip: IceIpFamily): string | null {
  if (path === 'turn') return 'T';
  const fam = ip === 'ipv6' ? '6' : ip === 'ipv4' ? '4' : null;
  if (!fam) return null;
  switch (path) {
    case 'lan':
      return `L${fam}`;
    case 'host':
      return `H${fam}`;
    case 'stun':
      return `S${fam}`;
    default:
      return null;
  }
}

function dialLabel(phase: 'stun' | 'turn' | null | undefined): string | null {
  if (phase === 'stun') return 'Connecting';
  if (phase === 'turn') return 'Re-trying';
  return null;
}

function IconSignal({ tone }: { tone: ConnTone }): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className={toneClass(tone)}>
      <rect x="4" y="3" width="16" height="18" rx="3" fill="currentColor" opacity="0.2" />
      <circle cx="12" cy="7" r="2.2" fill="currentColor" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" opacity="0.85" />
      <circle cx="12" cy="17" r="2.2" fill="currentColor" opacity="0.55" />
    </svg>
  );
}

function IconPc({ tone }: { tone: ConnTone }): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className={toneClass(tone)}>
      <rect x="3" y="4" width="18" height="12" rx="2" fill="currentColor" opacity="0.25" />
      <rect x="5" y="6" width="14" height="8" rx="1" fill="currentColor" />
      <rect x="9" y="17" width="6" height="2" rx="0.5" fill="currentColor" />
    </svg>
  );
}

function IconLink({ tone }: { tone: ConnTone }): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden className={toneClass(tone)}>
      <path
        d="M8.5 12.5l2-2a4 4 0 015.7 5.7l-1.4 1.4M15.5 11.5l-2 2a4 4 0 01-5.7-5.7l1.4-1.4"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function StatusPills({
  session,
  busy,
  signedIn,
  pcSessionActive,
  icePath,
  iceDialPhase,
  iceIpFamily,
  p2pDetail,
}: StatusPillsProps): JSX.Element {
  const sigTone = signalingTone(session.status, signedIn, pcSessionActive);
  const hostT = hostTone(
    session.hostOnline,
    pcSessionActive,
    session.status === 'open',
    session.phase
  );
  const p2pT = p2pTone(session.phase, busy, pcSessionActive, session.hostOnline);
  const linked = session.phase === 'linked';
  const badge =
    linked && icePath && !iceDialPhase ? pathBadge(icePath, iceIpFamily ?? null) : null;
  const dialText = !linked ? dialLabel(iceDialPhase) : null;
  const subLine = dialText ?? (linked ? null : p2pDetail);

  const statusLine = [badge, subLine].filter(Boolean).join(' · ');
  const p2pTitle = statusLine || 'Connection to your drive';

  return (
    <div className="conn-status" aria-label="Connection status">
      <div className="conn-icons">
        <span className="conn-slot" title="Signaling server">
          <IconSignal tone={sigTone} />
        </span>
        <span className="conn-slot" title="Desktop host">
          <IconPc tone={hostT} />
        </span>
        <span className="conn-slot" title={p2pTitle}>
          <IconLink tone={p2pT} />
        </span>
      </div>
      {statusLine ? (
        <p className="conn-status-line" aria-hidden>
          {statusLine}
        </p>
      ) : null}
    </div>
  );
}
